import usePosOrderDeliveryCost from "@/shared/atoms/usePosOrderDeliveryCostAtom";
import usePosOrderDeliveryMethod from "@/shared/atoms/usePosOrderDeliveryMethodAtom";
import usePosOrderServiceCost from "@/shared/atoms/usePosOrderServiceCostAtom";
import usePosOrderCustomDiscount from "@/shared/atoms/usePosOrderCustomDiscount";
import useCartValidatorValuesHook from "@/shared/hooks/useCartValidatorValuesHook";
import useCustomDiscountValue from "@/shared/hooks/useCustomDiscountValue";
import usePosOrderTipHook from "@/shared/hooks/usePosOrderTipHook";
import usePosOrderTotalAmountHook from "@/shared/hooks/usePosOrderTotalAmountHook";
import { useIsCartEmpty } from "@/shared/hooks/useCartHook";
import { useIsTableMethod } from "@/shared/hooks/usePosOrderTableSessionHook";
import { cn } from "@/shared/utils/cn";
import { formatPrice } from "@/shared/utils/helpers";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import PosCartTotal from "./PosCartTotal";
import PosRow from "./PosRow";
import HandoverTime from "./HandoverTime";
import useIsTablePos from "@/Pages/Auth/Bestellingen/PosOrder/useIsTablePos";
import Alert from "../Alert/Alert";

interface PosCartBreakdownRowProps {
  show: boolean;
  children: ReactNode;
}

const PosCartBreakdownRow = ({ show, children }: PosCartBreakdownRowProps) => (
  <div
    className={cn(
      "grid transition-[grid-template-rows,opacity] duration-300 ease-in-out motion-reduce:transition-none",
      show ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
    )}
  >
    <div className={cn("min-h-0 overflow-hidden border-b border-border/50")}>
      {children}
    </div>
  </div>
);

const formatDiscountEuro = (value: number) => {
  const amount = Math.abs(parseFloat(`${value}`) || 0);
  return formatPrice(amount);
};

const PosCartPriceBreakdown = ({
  isCartValidating,
}: {
  isCartValidating: boolean;
}) => {
  const { t } = useTranslation();
  const isCartEmpty = useIsCartEmpty();
  const isTableMethod = useIsTableMethod();
  const [deliveryMethod] = usePosOrderDeliveryMethod();
  const { totalDiscount, uniqueDiscountName, totalExtras, uniqueExtraName } =
    useCartValidatorValuesHook();
  const [deliveryCost] = usePosOrderDeliveryCost();
  const [serviceCost] = usePosOrderServiceCost();
  const { tip } = usePosOrderTipHook();
  const totalAmount = usePosOrderTotalAmountHook();
  const [{ fixed_price_discount, percentage_discount, type }] =
    usePosOrderCustomDiscount();
  const customDiscountValue = useCustomDiscountValue();
  const isTableOrder = useIsTablePos();
  const [error, setError] = useState<string>("");

  const hasCustomDiscount = Boolean(
    !isCartEmpty &&
    customDiscountValue > 0 &&
    (percentage_discount || fixed_price_discount)
  );

  return (
    <div className="relative z-20 mb-[0.4rem] overflow-hidden">
      <div
        className={cn(
          "flex flex-col transition-opacity duration-300 ease-in-out motion-reduce:transition-none",
          isCartValidating && "opacity-55"
        )}
      >
        <PosCartBreakdownRow show={!isTableMethod && totalExtras > 0}>
          <PosRow
            fontSize="md"
            label={`${t("posOrders.extras")} ${`[ ${uniqueExtraName?.join(
              ","
            )} ]`}`}
            fontWeight="semibold"
            hideMargin
            className="py-2.5"
          >
            {formatPrice(totalExtras)}
          </PosRow>
        </PosCartBreakdownRow>

        <PosCartBreakdownRow
          show={!isCartEmpty && !isTableMethod && totalDiscount < 0}
        >
          <PosRow
            fontSize="md"
            label={`${t("posOrders.discount")} ${`[ ${uniqueDiscountName?.join(
              ","
            )} ]`}`}
            fontWeight="semibold"
            color="error"
            hideMargin
            className="py-2.5"
          >
            {formatDiscountEuro(totalDiscount)}
          </PosRow>
        </PosCartBreakdownRow>

        <PosCartBreakdownRow show={hasCustomDiscount}>
          <PosRow
            fontSize="md"
            label={`${t("posOrders.discount")} ${`[ ${
              type === 1
                ? t("posOrders.percentage", { value: percentage_discount })
                : t("posOrders.amount")
            } ]`}`}
            fontWeight="semibold"
            color="error"
            hideMargin
            className="py-2.5"
          >
            {formatDiscountEuro(customDiscountValue)}
          </PosRow>
        </PosCartBreakdownRow>

        <PosCartBreakdownRow show={!isTableMethod && Number(deliveryCost) > 0}>
          <PosRow
            fontSize="md"
            label={t("posOrders.deliveryFee")}
            hideMargin
            className="py-2.5"
          >
            {formatPrice(deliveryCost)}
          </PosRow>
        </PosCartBreakdownRow>

        <PosCartBreakdownRow show={Number(serviceCost) > 0}>
          <PosRow
            fontSize="md"
            label={t("posOrders.serviceCost")}
            hideMargin
            className="py-2.5"
          >
            {formatPrice(serviceCost)}
          </PosRow>
        </PosCartBreakdownRow>

        <PosCartBreakdownRow show={tip !== "-1"}>
          <PosRow
            fontSize="md"
            label={t("posOrders.tip")}
            hideMargin
            className="py-2.5"
          >
            {formatPrice(tip)}
          </PosRow>
        </PosCartBreakdownRow>
      </div>

      {error && <Alert onClickClose={() => setError("")}>{error}</Alert>}

      <div
        className={cn(
          "transition-opacity duration-300 ease-in-out motion-reduce:transition-none mt-4",
          isCartValidating && "opacity-70"
        )}
      >
        <PosCartTotal total={totalAmount} />
        {!isTableOrder && <HandoverTime selectedDelivery={deliveryMethod} />}
      </div>
    </div>
  );
};

export default PosCartPriceBreakdown;
